import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import { supabase } from '../../lib/supabase';
import { projectsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import SearchFilters from '../../components/Search/SearchFilters';
import LikeButton from '../../components/Projects/LikeButton';
import ShareButton from '../../components/Projects/ShareButton';
import BadgeEntrepriseSuivie from '../../components/Projects/BadgeEntrepriseSuivie';

const Projects = () => {
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    stage: '',
    country: '',
    currency: '',
    minFunding: '',
    maxFunding: '',
    minROI: '',
    maxROI: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Requête via API backend (service key Supabase, contourne RLS)
  // React Query re-fetche automatiquement dès que la clé change
  const { data: projects = [], isLoading, refetch } = useQuery(
    ['projects', searchTerm, filters],
    async () => {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.stage)    params.stage = filters.stage;
      if (filters.country)  params.country = filters.country;
      if (searchTerm)       params.search = searchTerm;

      const response = await projectsAPI.getProjects(params);
      // L'intercepteur api.js extrait automatiquement response.data.data
      const data = Array.isArray(response.data) ? response.data : [];
      console.log(`✅ [API] ${data.length} projets chargés`);
      return data;
    },
    {
      staleTime: 0,
      refetchOnMount: true,
      onError: (error) => {
        console.error('Erreur chargement projets:', error);
        toast.error('Erreur lors du chargement des projets');
      },
    }
  );

  // Souscription Realtime — mise à jour instantanée à chaque INSERT/UPDATE/DELETE
  useEffect(() => {
    const channel = supabase
      .channel('public:bourse_projects')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bourse_projects' },
        (payload) => {
          console.log('[Realtime] Changement détecté:', payload.eventType);
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Récupérer les pays et devises via le serveur Express
  const { data: countries = [] } = useQuery('countries', async () => {
    try {
      const response = await axios.get('/api/countries');
      const result = response.data;
      // Backend retourne { success: true, data: [{code, name}] }
      if (result?.success && Array.isArray(result.data)) return result.data;
      return [];
    } catch {
      return [];
    }
  });

  const { data: currencies = [] } = useQuery('currencies', async () => {
    try {
      const response = await axios.get('/api/currencies');
      const result = response.data;
      // Backend retourne { success: true, currencies: [{code, name, symbol}] }
      if (result?.success && Array.isArray(result.currencies)) return result.currencies;
      return [];
    } catch {
      return [];
    }
  });

  // React Query re-fetche automatiquement grâce à la clé dynamique — pas besoin de refetch() manuel
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des projets...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text mb-4">
            Projets Innovants
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Découvrez les idées et projets les plus prometteurs de notre communauté
          </p>

          {isAuthenticated && (
            <Link
              to="/projects/create"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Soumettre un Projet
            </Link>
          )}
        </div>

        {/* Recherche et Filtres */}
        <SearchFilters
          onSearch={handleSearch}
          onFiltersChange={handleFiltersChange}
          countries={countries}
          currencies={currencies}
        />

        {/* Liste des projets */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-text mb-2">
              Aucun projet trouvé
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || Object.values(filters).some(v => v !== '' && v !== 'createdAt' && v !== 'desc')
                ? "Aucun projet ne correspond à vos critères de recherche."
                : "Aucun projet n'a encore été soumis. Soyez le premier !"
              }
            </p>
            {isAuthenticated && projects.length === 0 && (
              <Link
                to="/projects/create"
                className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Soumettre le premier projet
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => (
              <div key={project._id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                {/* Zone cliquable — titre, description, métadonnées */}
                <Link to={`/projects/${project._id}`} className="block p-6 pb-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {project.featuredUntil && (
                        <span className="inline-block mb-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                          Mis en avant
                        </span>
                      )}
                      <h3 className="text-xl font-semibold text-text mb-2 line-clamp-2">
                        {project.title}
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                          {project.category}
                        </span>
                        <span className="px-2 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full">
                          {project.stage}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Financement recherché:</span>
                      <span className="font-semibold text-text">
                        {formatCurrency(project.funding?.amountNeeded, project.funding?.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Pays:</span>
                      <span className="font-medium text-text">{project.location?.country || 'Non spécifié'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Auteur:</span>
                      <span className="font-medium text-text">{project.owner?.firstName} {project.owner?.lastName}</span>
                    </div>
                    {project.ownerSuivi && (
                      <div className="flex justify-end"><BadgeEntrepriseSuivie /></div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{formatDate(project.createdAt)}</span>
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {project.views || 0}
                      </span>
                    </div>
                  </div>
                </Link>

                {/* Zone actions — Like, Share, Voir le projet */}
                <div className="px-6 pb-6">
                  <div className="flex items-center justify-between mb-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <LikeButton
                        projectId={project._id}
                        initialLikeCount={project.likeCount || 0}
                        initialIsLiked={false}
                      />
                      <ShareButton project={project} />
                    </div>
                  </div>

                  <Link
                    to={`/projects/${project._id}`}
                    className="block w-full text-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Voir le projet
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
